import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Lock, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TRIP_TYPES, waPackage, waLink } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "h-11 rounded-xl border-slate-200 bg-white focus-visible:ring-skybrand";

export default function EnquiryForm({ preset = {}, source = "website", onDone, compact = false }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    destination: preset.destination || "",
    travel_date: "", adults: 2, children: 0, infants: 0,
    trip_type: preset.tripType || "Other",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      destination: preset.destination || f.destination,
      trip_type: preset.tripType || f.trip_type,
    }));
  }, [preset.destination, preset.tripType]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        adults: Number(form.adults) || 0,
        children: Number(form.children) || 0,
        infants: Number(form.infants) || 0,
        package_name: preset.packageName || "",
        source,
      });
      setDone(true);
    } catch (err) {
      toast.error("Could not submit your enquiry. Please try WhatsApp or call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const wa = preset.packageName
      ? waPackage(preset.packageName)
      : waLink(`Hi D Travel Link, I just submitted an enquiry${form.destination ? ` for ${form.destination}` : ""}. Please help me plan my holiday.`);
    return (
      <div data-testid="enquiry-success" className="text-center py-6">
        <div className="mx-auto w-16 h-16 rounded-full bg-sky-50 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-skybrand" />
        </div>
        <h3 className="mt-4 font-heading text-2xl font-bold text-slate-900">Thank you, {form.name.split(" ")[0]}!</h3>
        <p className="mt-2 text-slate-600 text-sm leading-relaxed max-w-sm mx-auto">
          Your enquiry has been received. A D Travel Link expert from Thane will contact you within 2 business hours with a tailor-made quote.
        </p>
        <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="enquiry-success-whatsapp"
           className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1fb857] transition-colors">
          <MessageCircle className="w-4 h-4" /> Continue on WhatsApp
        </a>
        {onDone && (
          <button onClick={onDone} data-testid="enquiry-success-close" className="mt-3 block mx-auto text-sm text-slate-500 hover:text-slate-900 underline underline-offset-4">
            Close
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
      {preset.packageName && (
        <div className="rounded-xl bg-sky-50 border border-sky-100 px-4 py-3 text-sm text-slate-700">
          Enquiring for: <span className="font-semibold text-navy">{preset.packageName}</span>
        </div>
      )}
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <Label htmlFor="eq-name">Full Name *</Label>
          <Input id="eq-name" data-testid="enquiry-form-name-input" required minLength={2} placeholder="Your full name"
                 value={form.name} onChange={set("name")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="eq-phone">Mobile Number *</Label>
          <Input id="eq-phone" data-testid="enquiry-form-phone-input" required minLength={8} type="tel" placeholder="+91 ..."
                 value={form.phone} onChange={set("phone")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="eq-email">Email *</Label>
          <Input id="eq-email" data-testid="enquiry-form-email-input" required type="email" placeholder="you@example.com"
                 value={form.email} onChange={set("email")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="eq-destination">Destination</Label>
          <Input id="eq-destination" data-testid="enquiry-form-destination-select" placeholder="e.g. Singapore, Kashmir, Europe"
                 value={form.destination} onChange={set("destination")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="eq-date">Travel Date</Label>
          <Input id="eq-date" data-testid="enquiry-form-date-input" type="date"
                 value={form.travel_date} onChange={set("travel_date")} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="eq-type">Trip Type</Label>
          <select id="eq-type" data-testid="enquiry-form-trip-type-select" value={form.trip_type} onChange={set("trip_type")}
                  className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-skybrand">
            {TRIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3 sm:col-span-2">
          <div>
            <Label htmlFor="eq-adults">Adults</Label>
            <Input id="eq-adults" data-testid="enquiry-form-adults-input" type="number" min={0} max={40}
                   value={form.adults} onChange={set("adults")} className={inputCls} />
          </div>
          <div>
            <Label htmlFor="eq-children">Children</Label>
            <Input id="eq-children" data-testid="enquiry-form-children-input" type="number" min={0} max={40}
                   value={form.children} onChange={set("children")} className={inputCls} />
          </div>
          <div>
            <Label htmlFor="eq-infants">Infants</Label>
            <Input id="eq-infants" data-testid="enquiry-form-infants-input" type="number" min={0} max={10}
                   value={form.infants} onChange={set("infants")} className={inputCls} />
          </div>
        </div>
      </div>
      <div>
        <Label htmlFor="eq-message">Message</Label>
        <Textarea id="eq-message" data-testid="enquiry-form-message-input" rows={3}
                  placeholder="Tell us about your trip — hotel preference, budget, special occasions..."
                  value={form.message} onChange={set("message")} className="rounded-xl border-slate-200" />
      </div>
      <Button type="submit" disabled={submitting} data-testid="enquiry-form-submit-button"
              className="w-full h-12 rounded-full bg-navy text-white font-heading font-bold tracking-wide hover:bg-royal transition-colors">
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "REQUEST A QUOTE"}
      </Button>
      <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
        <Lock className="w-3 h-3" /> 100% confidential. No spam — our Thane travel experts respond within 2 business hours.
      </p>
    </form>
  );
}
